import { useEffect, useState } from "react"

import { Permissions } from "javascripts/types"
import ErrorBoundary from "shared/components/error_boundary"
import { PlanId } from "shared/utils/tiers"
import {
  ProPaywallTrigger,
  trackProPaywallDismissed,
  trackProPaywallPlanToggled,
  trackProPaywallPurchaseCompleted,
  trackProPaywallPurchaseStarted,
  trackProPaywallShown,
} from "app/utils/mixpanel_tracking"
import { PlanLogo, formatCreditsPerMonth } from "app/components/credits_upgrade/plan_summary"
import { Currency } from "app/components/subscriptions/currency_switcher_modal"
import PaymentInfoForm, { PlanType } from "app/components/subscriptions/payment_info_form"
import Success from "app/components/subscriptions/success"

import Carousel, { Slide } from "./carousel"

import styles from "./pro_paywall.module.scss"

interface SlideImages {
  advancedAutomations: string
  fasterSpeeds: string
  services: string
  moreCredits: string
}

interface Subscription {
  expires_at: string | null
  payment_interval: PlanType["interval"]
  plan_id: PlanId
  subscription_id: string
}

interface Props {
  creditsPerMonth: number | null
  currencies: Currency[]
  currencyPlans: Record<string, { monthly_plan: PlanType; yearly_plan: PlanType }>
  defaultCurrency: string
  email: string
  initialInterval: PlanType["interval"]
  initialPromoCode: string | null
  planId: PlanId
  productId: string
  setupIntentClientSecret: string | null
  setupIntentReturnUrl: string | null
  showCurrencySwitcher: boolean
  slideImages: SlideImages
  stripeCustomerPreinitialized: boolean
  tier: string
  trigger: ProPaywallTrigger | null
  urls: {
    createStripeCustomerUrl: string
    explore: string
    returnTo: string
    subscriptionsPath: string
    subscriptionSuccessPath: string
  }
}

const slidesFor = (images: SlideImages): Slide[] => [
  {
    title: "Power more advanced automations",
    body: "Use credits to power your automations, including actions that require more processing.",
    image: { src: images.advancedAutomations, width: 457, height: 400 },
  },
  {
    title: "Access faster speeds and higher rate limits",
    body: "You’ll get faster updates and more actions with increased rate limits and more frequent checks.",
    image: { src: images.fasterSpeeds, width: 400, height: 400 },
  },
  {
    title: "Connect to our 1,000+ services and apps",
    body: "Get more access to the different ways you can connect the tools you use every day in your life and business.",
    image: { src: images.services, width: 576, height: 420 },
  },
  {
    title: "More credits, more possibilities",
    body: "With your increased credits per month, you can now power more automation activity.",
    image: { src: images.moreCredits, width: 576, height: 420 },
  },
]

const ProPaywall = (props: Props) => {
  const { trigger, urls } = props
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [permissions, setPermissions] = useState<Permissions | null>(null)

  useEffect(() => {
    trackProPaywallShown(trigger)
  }, [trigger])

  const dismiss = () => {
    trackProPaywallDismissed(trigger)
    window.location.assign(urls.returnTo)
  }

  const onSubscribed = (subscribed: Subscription) => {
    trackProPaywallPurchaseCompleted(trigger, subscribed.payment_interval)
    setSubscription(subscribed)
  }

  if (subscription && permissions) {
    return (
      <ErrorBoundary>
        <Success
          urls={{ continuePath: urls.returnTo, explore: urls.explore }}
          subscription={{
            isTrialing: false,
            expiresAt: subscription.expires_at,
            plan: subscription.plan_id,
            subscriptionId: subscription.subscription_id,
          }}
          permissions={permissions}
          subscriptionMobileSuccessPath=""
        />
      </ErrorBoundary>
    )
  }

  const notNowButton = (className: string) => (
    <button type="button" className={className} onClick={dismiss}>
      Not now
    </button>
  )

  return (
    <ErrorBoundary>
      <div className={styles["pro-paywall"]}>
        <header className={styles["mobile-header"]}>{notNowButton(styles["not-now"])}</header>

        <div className={styles["carousel"]}>
          <Carousel slides={slidesFor(props.slideImages)} />
        </div>

        <div className={styles["intro"]}>
          <h1 className={styles["heading"]}>
            Get started with
            <PlanLogo tier={props.tier} className={styles["plan-logo"]} />
          </h1>
          {props.creditsPerMonth !== null && (
            <div className={styles["credits-allowance"]}>{formatCreditsPerMonth(props.creditsPerMonth)}</div>
          )}
          <p className={styles["subtitle"]}>Subscribe to get more credits per month to power your automations.</p>
        </div>

        <div className={styles["checkout"]}>
          <PaymentInfoForm
            creditsCheckout
            ctaCopy="Upgrade to Pro"
            currencies={props.currencies}
            currencyPlans={props.currencyPlans}
            defaultCurrency={props.defaultCurrency}
            eligibleForTrial={false}
            email={props.email}
            initialInterval={props.initialInterval}
            initialPromoCode={props.initialPromoCode}
            modalButtonType="new"
            onIntervalChange={interval => trackProPaywallPlanToggled(trigger, interval)}
            onModalHandleSuccessfulSubscription={() => {}}
            onPurchaseStarted={interval => trackProPaywallPurchaseStarted(trigger, interval)}
            planId={props.planId}
            productId={props.productId}
            redirectToMobileAppOnSuccess={false}
            referrerPath={urls.returnTo}
            renderingInsideModal={false}
            secondaryAction={notNowButton(styles["desktop-not-now"])}
            setPermissions={granted => setPermissions(granted as Permissions)}
            setSubscription={subscribed => onSubscribed(subscribed as Subscription)}
            setupIntentClientSecret={props.setupIntentClientSecret}
            setupIntentReturnUrl={props.setupIntentReturnUrl}
            showCancelAnytimeFineprint
            showCurrencySwitcher={props.showCurrencySwitcher}
            skipAbandonedCheckoutEmail
            stripeCustomerPreinitialized={props.stripeCustomerPreinitialized}
            tier={props.tier}
            urls={{
              createStripeCustomerUrl: urls.createStripeCustomerUrl,
              subscriptionsPath: urls.subscriptionsPath,
              subscriptionSuccessPath: urls.subscriptionSuccessPath,
            }}
          />
        </div>
      </div>
    </ErrorBoundary>
  )
}

export default ProPaywall
